import { useEffect, useRef } from 'react'
import { usePlatform } from './usePlatform'

/** Der Kanal, über den das Menü „Bearbeiten › Suchen …“ (⌘K) die Suche umschaltet. */
export const TOGGLE_SEARCH_CHANNEL = 'app:toggle-search'

/**
 * Mit ⌘ (Mac) bzw. Strg (sonst) und sonst nichts — ⌥⌘K & Co. gehören anderen. Auf dem Mac NICHT
 * mit ⌃: ⌃K löscht in Textfeldern bis zum Zeilenende, ⌃F rückt den Cursor vor (Diff-Audit 02.10.2026).
 */
function isModKey(e: KeyboardEvent, code: string, isMac: boolean): boolean {
  const mod = isMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey
  return mod && !e.altKey && !e.shiftKey && e.code === code
}

/**
 * **⌘K / Strg+K schaltet die Suche um** (#233) — auch aus einem Textfeld heraus. Auf dem Desktop
 * zusätzlich **⌘F / Strg+F** (gewünscht 02.10.2026); im Web bleibt ⌘F die Suche des Browsers.
 *
 * In Electron kommt ⌘K über das Menü (`menuService.ts`, IPC), damit es dort steht und vor jedem
 * Feld greift; der Renderer deutet die Taste dann nicht selbst, sonst schaltete ein Druck zweimal
 * um, und nichts geschähe. ⌘F hat keinen Menüeintrag — ein zweiter „Suchen …“ stünde doppelt da —
 * und wird wie im Web ⌘K per `keydown` in der Erfassungsphase abgefangen, bevor ein Editor es bekommt.
 */
export function useCommandPaletteShortcut(onToggle: () => void, enabled = true): void {
  const toggleRef = useRef(onToggle)
  toggleRef.current = onToggle
  const { isMac } = usePlatform()

  useEffect(() => {
    if (!enabled) return
    const electron = !!window.api?.receive
    const unsubscribe = electron ? window.api.receive(TOGGLE_SEARCH_CHANNEL, () => toggleRef.current()) : undefined
    const keyCode = electron ? 'KeyF' : 'KeyK'
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isModKey(e, keyCode, isMac)) return
      e.preventDefault()
      toggleRef.current()
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => {
      unsubscribe?.()
      window.removeEventListener('keydown', onKeyDown, true)
    }
  }, [enabled, isMac])
}
